/**
 * MVP-497: real fullscreen for a room PC — the browser's Fullscreen API AND the app's own chrome
 * (AppHeader 52px + StatusBar 28px) out of the way, so the catalog gets the whole panel.
 *
 * The target is `document.documentElement`, NOT `.library-screen`. `ui/Portal.tsx` renders every
 * `Dialog`/`Sheet` into `document.body`, and a browser paints only the fullscreen element's own
 * subtree: fullscreening the screen div would leave `SingerPicker`, `ShortcutSheet` and the
 * delete-confirm dialog rendering OUTSIDE it and therefore invisible. The operator would press
 * "Play now", a confirm dialog would open off-screen, and the app would look frozen.
 *
 * The chrome half is CSS, not React state. Because the fullscreen element is `<html>`,
 * `html:fullscreen` matches, and the two rules that hide the chrome live in `App.css` keyed on the
 * browser's own state. `:fullscreen` stops matching the instant the browser leaves fullscreen — by
 * this button, by ESC, by F11, by the browser's own UI, by a policy revoking it — and the chrome
 * reappears in the same frame. There is no listener to miss and no setState to skip, so no code
 * path can exist that leaves a room PC showing a catalog with no navigation. It also means F11
 * already hides the app chrome for free, and that a refused `requestFullscreen()` no-ops entirely
 * (nothing hides, because `:fullscreen` never matched). `App.tsx`, which renders both bars, stays
 * completely fullscreen-unaware.
 *
 * Not persisted, for the same reason `useCockpitVideoSize.ts` does not persist its fullscreen: a
 * page that reloaded itself into fullscreen with no user gesture would be refused anyway.
 */
import { useCallback, useEffect, useState } from 'react';

/** Exists so the ESC case is nameable in a test: after the browser leaves fullscreen on its own,
 * `document.fullscreenElement` is null and the button must be offering to enter again. */
export function isDocumentFullscreen(fullscreenElement: Element | null): boolean {
  return fullscreenElement !== null;
}

export function fullscreenToggleIntent(isFullscreen: boolean): 'enter' | 'exit' {
  return isFullscreen ? 'exit' : 'enter';
}

export interface FullscreenControl {
  supported: boolean;
  isFullscreen: boolean;
  toggle: () => void;
}

export function useFullscreen(): FullscreenControl {
  // `document` exists in every real browser but not under vitest's node environment — same guard
  // shape as `useCockpitVideoSize.ts`'s `localStorage` read. Read once: whether the document is
  // ALLOWED to go fullscreen is a property of the frame, not something that changes under us.
  const [supported] = useState(() => typeof document !== 'undefined' && document.fullscreenEnabled === true);
  const [isFullscreen, setIsFullscreen] = useState(false);

  // `fullscreenchange` on `document` is the only source of truth, and `sync()` runs once before the
  // listener is registered: Library is mounted-but-hidden across view switches (`App.tsx`), so it
  // can mount while the document is ALREADY fullscreen, and a hook that only ever learned from
  // subsequent events would start on the wrong icon.
  useEffect(() => {
    const sync = () => setIsFullscreen(isDocumentFullscreen(document.fullscreenElement));
    sync();
    document.addEventListener('fullscreenchange', sync);
    return () => document.removeEventListener('fullscreenchange', sync);
  }, []);

  const toggle = useCallback(() => {
    // Live read, not the React state: ESC/F11 may have changed it since the last render, and this
    // handler must act on what the browser is doing rather than on what we last heard.
    // ponytail: no vendor-prefixed fallbacks. This app is Chrome-on-a-KJ-machine (SPEC §2), and
    // unprefixed Fullscreen has been the only spelling Chrome needs for years.
    const intent = fullscreenToggleIntent(isDocumentFullscreen(document.fullscreenElement));
    // The rejection is swallowed on purpose. A refused request changes nothing — the chrome is CSS,
    // so it never hid — and there is no state here worth correcting or interrupting the KJ over.
    if (intent === 'exit') void document.exitFullscreen().catch(() => {});
    else void document.documentElement.requestFullscreen().catch(() => {});
  }, []);

  return { supported, isFullscreen, toggle };
}
